import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PenTool, CheckSquare, Trash2, Save, Undo, Loader2, AlertCircle } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const SignatureSection = ({
    agreementText = "I confirm that I have read and understood the content of this task/training. I certify that the work submitted is my own and I agree to the terms and conditions.",
    signatureData,
    onSignatureChange,
    isSubmitting = false,
    error,
    readOnly = false,
    showValidationError = false
}) => {
    const [isChecked, setIsChecked] = useState(!!signatureData);
    const [isUploading, setIsUploading] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const sigCanvas = useRef(null);
    const containerRef = useRef(null);

    const isSigned = signatureData && (signatureData.startsWith('http') || signatureData.startsWith('data:'));

    const resizeCanvas = useCallback(() => {
        if (containerRef.current && sigCanvas.current) {
            const container = containerRef.current;
            const canvas = sigCanvas.current.getCanvas();
            const rect = container.getBoundingClientRect();
            const width = rect.width;
            const height = Math.max(140, Math.min(180, window.innerHeight * 0.22));
            const currentWidth = parseInt(canvas.style.width) || 0;
            const currentHeight = parseInt(canvas.style.height) || 0;

            if (width > 0 && (Math.abs(width - currentWidth) > 1 || Math.abs(height - currentHeight) > 1)) {
                const currentData = !sigCanvas.current.isEmpty() ? sigCanvas.current.toDataURL() : null;
                canvas.width = width * window.devicePixelRatio;
                canvas.height = height * window.devicePixelRatio;
                canvas.style.width = `${width}px`;
                canvas.style.height = `${height}px`;
                const ctx = canvas.getContext('2d');
                ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
                if (currentData) sigCanvas.current.fromDataURL(currentData);
            }
        }
    }, []);

    useEffect(() => {
        const initialTimeout = setTimeout(resizeCanvas, 50);
        const handleResize = () => setTimeout(resizeCanvas, 100);
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        return () => {
            clearTimeout(initialTimeout);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [resizeCanvas]);

    useEffect(() => {
        if (signatureData) setIsChecked(true);
    }, [signatureData]);

    const handleCheckChange = (e) => {
        setIsChecked(e.target.checked);
        if (!e.target.checked) {
            onSignatureChange('');
            if (sigCanvas.current) sigCanvas.current.clear();
            setIsEmpty(true);
        }
    };

    const clearSignature = () => {
        if (sigCanvas.current) sigCanvas.current.clear();
        setIsEmpty(true);
        onSignatureChange('');
    };

    const handleSignatureEnd = () => {
        if (sigCanvas.current) setIsEmpty(sigCanvas.current.isEmpty());
    };

    const handleSignatureBegin = () => {
        if (!isChecked && sigCanvas.current) sigCanvas.current.clear();
    };

    const saveSignature = async () => {
        if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
            toast.error("Please sign before saving");
            return;
        }
        setIsUploading(true);
        try {
            const dataURL = sigCanvas.current.toDataURL('image/png');
            const res = await fetch(dataURL);
            const blob = await res.blob();
            const file = new File([blob], `signature_${Date.now()}.png`, { type: "image/png" });
            const formData = new FormData();
            formData.append('files', file);
            const token = localStorage.getItem('token');
            const response = await api.post('/files/upload-proof', formData, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            if (response.data.success && response.data.files.length > 0) {
                onSignatureChange(response.data.files[0].file_url);
                toast.success('Signature saved!');
            } else {
                toast.error('Failed to upload signature');
            }
        } catch (error) {
            console.error('Signature upload error:', error);
            toast.error('Failed to save signature');
        } finally {
            setIsUploading(false);
        }
    };

    const hasError = error || (showValidationError && !isSigned && isEmpty);
    const errorMessage = error || (showValidationError && !isSigned ? 'Signature required' : null);

    return (
        <div className="space-y-4">
            {/* Agreement */}
            <div className={`flex items-start gap-3 p-4 rounded-xl border ${hasError ? 'border-red-200 dark:border-red-500/20 bg-red-50/50 dark:bg-red-500/5' : 'border-gray-200 dark:border-slate-700/60 bg-gray-50/50 dark:bg-slate-800/30'}`}>
                <div className="pt-0.5 flex-shrink-0">
                    <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                        checked={isChecked}
                        onChange={handleCheckChange}
                        id="signature-agreement"
                        disabled={isSubmitting || isSigned || readOnly}
                    />
                </div>
                <label htmlFor="signature-agreement" className={`text-sm text-gray-600 dark:text-slate-400 leading-relaxed ${!readOnly ? 'cursor-pointer' : ''} select-none`}>
                    {agreementText}
                </label>
            </div>

            {errorMessage && (
                <p className="text-xs text-red-600 dark:text-red-400 font-medium flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {errorMessage}
                </p>
            )}

            {/* Signature Canvas Area */}
            <div className={`transition-all duration-300 ${(!isChecked && !readOnly) ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                <div
                    ref={containerRef}
                    className={`relative rounded-xl overflow-hidden ${readOnly ? '' : 'border-2 border-dashed'} ${hasError && !isSigned ? 'border-red-300 dark:border-red-500/30' : 'border-gray-200 dark:border-slate-700'}`}
                >
                    {isSigned ? (
                        <div className="relative w-full flex items-center justify-center bg-white dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700" style={{ minHeight: '140px' }}>
                            <img src={signatureData} alt="Signature" className="max-h-32 max-w-full object-contain p-4" />
                            {!readOnly && (
                                <button
                                    onClick={() => { onSignatureChange(''); setIsEmpty(true); }}
                                    className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                                    disabled={isSubmitting}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ) : readOnly ? (
                        <div className="w-full flex flex-col items-center justify-center bg-gray-50 dark:bg-slate-800/50 rounded-xl text-gray-400 dark:text-slate-500" style={{ minHeight: '100px' }}>
                            <PenTool className="w-6 h-6 mb-1 opacity-50" />
                            <p className="text-sm">No signature provided</p>
                        </div>
                    ) : (
                        <div className="relative w-full touch-none bg-white dark:bg-slate-900" style={{ minHeight: '140px' }}>
                            {isEmpty && isChecked && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                                    <span className="text-gray-300 dark:text-slate-600 text-base font-medium select-none">Sign here</span>
                                </div>
                            )}
                            {!isChecked && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center">
                                    <div className="bg-white/90 dark:bg-slate-900/90 px-3 py-1.5 rounded-lg text-sm text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-slate-700 flex items-center gap-1.5">
                                        <CheckSquare className="w-3.5 h-3.5" />
                                        Check the box above to sign
                                    </div>
                                </div>
                            )}
                            <SignatureCanvas
                                ref={sigCanvas}
                                penColor="black"
                                minWidth={1.5}
                                maxWidth={3}
                                velocityFilterWeight={0.7}
                                canvasProps={{
                                    className: 'w-full rounded-xl cursor-crosshair touch-none',
                                    style: { touchAction: 'none', minHeight: '140px' }
                                }}
                                onBegin={handleSignatureBegin}
                                onEnd={handleSignatureEnd}
                            />
                            {!isEmpty && (
                                <button
                                    onClick={clearSignature}
                                    className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors border border-gray-200 dark:border-slate-700 text-xs"
                                    type="button"
                                    disabled={isSubmitting}
                                >
                                    <Undo className="w-3 h-3" /> Clear
                                </button>
                            )}
                            <div className="absolute bottom-3 left-4 right-4 pointer-events-none">
                                <div className="border-b border-gray-200 dark:border-slate-700"></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Save Button */}
                {!isSigned && isChecked && !readOnly && (
                    <div className="mt-3 flex justify-end">
                        <button
                            onClick={saveSignature}
                            disabled={isUploading || isSubmitting || isEmpty}
                            className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isUploading ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                            ) : (
                                <><Save className="w-4 h-4" /> Save Signature</>
                            )}
                        </button>
                    </div>
                )}

                {!isSigned && !readOnly && isEmpty && isChecked && (
                    <p className="text-xs text-gray-400 dark:text-slate-500 mt-2 text-center">
                        Draw your signature using your mouse, finger, or stylus
                    </p>
                )}
            </div>
        </div>
    );
};

export default SignatureSection;
